import {useCallback, useRef, useState} from 'react'
import {useMutation} from '@tanstack/react-query'
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {ProgressBar} from '@/components/ui/progress-bar'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Stack} from '@/components/shared/Stack'
import {useAppConfig} from '@/hooks/use-app-config'
import {usePageTitle} from '@/hooks/use-page-title'
import {toast} from '@/hooks/use-toast'
import {generateSampleFrontendErrors} from '@/api/error-reporter'
import type {AttributeDefinition} from '@/types/attribute-definition'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {SectionContainer} from '@/components/shared/SectionContainer'


/* eslint-disable i18next/no-literal-string -- type definitions */
const GeneratorType = {
    PARTIES: 'parties',
    IDENTITIES: 'identities',
} as const
type GeneratorType = typeof GeneratorType[keyof typeof GeneratorType]

const AttrEntityType = {
    IDENTITY: 'IDENTITY',
    PARTY: 'PARTY',
    RESOURCE: 'RESOURCE',
    FUNCTION_GROUP: 'FUNCTION_GROUP',
} as const
type AttrEntityType = typeof AttrEntityType[keyof typeof AttrEntityType]
const GENERATOR_ATTRIBUTE_VALUES = 'attribute-values'
const DEFAULT_VALUE_TEMPLATE = 'TestValue-{{seq}}'
/* eslint-enable i18next/no-literal-string */

const ENTITY_TYPE_OPTIONS: AttrEntityType[] = [
    AttrEntityType.IDENTITY,
    AttrEntityType.PARTY,
    AttrEntityType.RESOURCE,
    AttrEntityType.FUNCTION_GROUP,
]

export function TestDataGenerator() {
    const i18n = useI18n()
    usePageTitle(i18n.testData.pageTitle)
    const config = useAppConfig()
    const services = useServices()

    const [generatorType, setGeneratorType] = useState<GeneratorType>(GeneratorType.IDENTITIES)
    const [entityCount, setEntityCount] = useState(100)
    const [partyType, setPartyType] = useState('PERSON')
    const [identityType, setIdentityType] = useState('EMPLOYEE')

    const [attrEntityType, setAttrEntityType] = useState<AttrEntityType>(AttrEntityType.IDENTITY)
    const [definitionId, setDefinitionId] = useState('')
    const [valueTemplate, setValueTemplate] = useState(DEFAULT_VALUE_TEMPLATE)

    const [entityGenerating, setEntityGenerating] = useState(false)
    const [entityProgress, setEntityProgress] = useState(0)
    const entityAbortRef = useRef(false)

    const handleGenerateEntities = useCallback(async () => {
        const batchSize = 1000
        const totalBatches = Math.ceil(entityCount / batchSize)
        let totalCreated = 0

        setEntityGenerating(true)
        setEntityProgress(0)
        entityAbortRef.current = false

        try {
            for (let batch = 0; batch < totalBatches; batch++) {
                if (entityAbortRef.current) break
                const remaining = entityCount - batch * batchSize
                const currentBatchSize = Math.min(batchSize, remaining)

                const response = await services.admin.generateTestData({
                    generator: generatorType,
                    count: currentBatchSize,
                    config: {
                        partyType,
                        ...(generatorType === GeneratorType.IDENTITIES ? {identityType} : {}),
                    },
                })
                totalCreated += (response as {created: number}).created
                setEntityProgress(Math.round(((batch + 1) / totalBatches) * 100))
            }
            toast({description: i18n.testData.success({count: totalCreated, type: generatorType})})
        } catch (err) {
            toast({description: i18n.testData.error, variant: 'destructive'})
            throw err
        } finally {
            setEntityGenerating(false)
        }
    }, [entityCount, generatorType, partyType, identityType, i18n, services.admin])

    const attrMutation = useMutation({
        mutationFn: (data: {generator: string; count: number; config: Record<string, string>}) =>
            services.admin.generateTestData(data),
        onSuccess: (response) => {
            toast({description: i18n.testData.success({count: (response as {created: number}).created, type: i18n.testData.attributeValues})})
        },
        onError: () => {
            toast({description: i18n.testData.error, variant: 'destructive'})
        },
    })

    const errorLogMutation = useMutation({
        mutationFn: async () => {
            generateSampleFrontendErrors()
            return services.errorLogs.generateTestData() as Promise<{generated: number}>
        },
        onSuccess: (data) => {
            toast({description: i18n.testData.success({count: data.generated + 8, type: i18n.testData.errorLogs})})
        },
        onError: () => {
            toast({description: i18n.testData.error, variant: 'destructive'})
        },
    })

    const handleGenerateAttributes = () => {
        attrMutation.mutate({
            generator: GENERATOR_ATTRIBUTE_VALUES,
            count: 0,
            config: {
                entityType: attrEntityType,
                definitionId,
                valueTemplate,
            },
        })
    }

    const getDefinitionsForType = (entityType: AttrEntityType): AttributeDefinition[] => {
        switch (entityType) {
            case AttrEntityType.IDENTITY:
                return config.identityAttributeDefinitions
            case AttrEntityType.PARTY:
                return config.partyAttributeDefinitions
            case AttrEntityType.RESOURCE:
                return config.resourceAttributeDefinitions
            case AttrEntityType.FUNCTION_GROUP:
                return config.functionGroupAttributeDefinitions
            default:
                return []
        }
    }

    const definitions = getDefinitionsForType(attrEntityType)

    const handleDefinitionChange = (newDefinitionId: string) => {
        setDefinitionId(newDefinitionId)
        const selectedDef = definitions.find((d) => d.id === newDefinitionId)
        if (selectedDef?.validationRules?.pattern) {
            setValueTemplate(selectedDef.validationRules.pattern)
        } else {
            setValueTemplate(DEFAULT_VALUE_TEMPLATE)
        }
    }

    return (
        <SectionContainer>
            <Stack gap="lg"> {/* 2 classes, below threshold — outer page container */}
                {/* Step 1: Generate Entities */}
                <Card data-testid="generate-entities-card">
                    <CardHeader>
                        <CardTitle data-testid="card-title-generate-entities">{i18n.testData.generateEntities}</CardTitle>
                        <CardDescription>{i18n.testData.subtitle}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <DetailGrid columns={4}>
                            <Stack gap="sm">
                                <Label htmlFor="generator-type">{i18n.testData.generatorType}</Label>
                                <Select
                                    value={generatorType}
                                    onValueChange={(v) => setGeneratorType(v as GeneratorType)}
                                >
                                    <SelectTrigger id="generator-type" data-testid="select-generator-type">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="identities">{i18n.testData.identities}</SelectItem>
                                        <SelectItem value="parties">{i18n.testData.parties}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Stack>
                            <Stack gap="sm">
                                <Label htmlFor="entity-count">{i18n.testData.count}</Label>
                                <Input
                                    id="entity-count"
                                    type="number"
                                    min={1}
                                    max={50000}
                                    value={entityCount}
                                    onChange={(e) => setEntityCount(Number(e.target.value))}
                                    data-testid="input-entity-count"
                                />
                            </Stack>
                            <Stack gap="sm">
                                <Label htmlFor="party-type">{i18n.testData.partyType}</Label>
                                <Select value={partyType} onValueChange={setPartyType}>
                                    <SelectTrigger id="party-type" data-testid="select-party-type">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {config.partyTypes.map((pt) => (
                                            <SelectItem key={pt.code} value={pt.code}>
                                                {pt.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Stack>
                            {generatorType === GeneratorType.IDENTITIES && (
                                <Stack gap="sm">
                                    <Label htmlFor="identity-type">{i18n.testData.identityType}</Label>
                                    <Select value={identityType} onValueChange={setIdentityType}>
                                        <SelectTrigger id="identity-type" data-testid="select-identity-type">
                                            <SelectValue/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {config.identityTypes.map((it) => (
                                                <SelectItem key={it.code} value={it.code}>
                                                    {it.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Stack>
                            )}
                        </DetailGrid>
                        <Stack gap="sm" className="mt-4">
                            <InlineRow gap="lg">
                                <Button
                                    onClick={handleGenerateEntities}
                                    disabled={entityGenerating}
                                    data-testid="btn-generate-entities"
                                >
                                    {entityGenerating && <ButtonSpinner/>}
                                    {entityGenerating ? i18n.testData.generating : i18n.testData.generate}
                                </Button>
                            </InlineRow>
                            {entityGenerating && (
                                <Stack className="max-w-md" data-testid="entity-progress">
                                    <ProgressBar
                                        value={entityProgress}
                                        label={i18n.testData.generatingProgress({
                                            current: Math.ceil((entityProgress / 100) * Math.ceil(entityCount / 1000)),
                                            total: Math.ceil(entityCount / 1000),
                                        })}
                                    />
                                </Stack>
                            )}
                        </Stack>
                    </CardContent>
                </Card>

                {/* Step 2: Generate Attribute Values */}
                <Card data-testid="generate-attributes-card">
                    <CardHeader>
                        <CardTitle data-testid="card-title-generate-attributes">{i18n.testData.generateAttributeValues}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <DetailGrid columns={3}>
                            <Stack gap="sm">
                                <Label htmlFor="attr-entity-type">{i18n.testData.entityType}</Label>
                                <Select
                                    value={attrEntityType}
                                    onValueChange={(v) => {
                                        setAttrEntityType(v as AttrEntityType)
                                        setDefinitionId('')
                                        setValueTemplate(DEFAULT_VALUE_TEMPLATE)
                                    }}
                                >
                                    <SelectTrigger id="attr-entity-type" data-testid="select-attr-entity-type">
                                        <SelectValue placeholder={i18n.testData.selectEntityType}/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ENTITY_TYPE_OPTIONS.map((et) => (
                                            <SelectItem key={et} value={et}>{et}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Stack>
                            <Stack gap="sm">
                                <Label htmlFor="attr-definition">{i18n.testData.definition}</Label>
                                <Select value={definitionId} onValueChange={handleDefinitionChange}>
                                    <SelectTrigger id="attr-definition" data-testid="select-definition">
                                        <SelectValue placeholder={i18n.testData.selectDefinition}/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {definitions.length === 0 ? (
                                            <SelectItem value="none" disabled>
                                                {i18n.testData.noDefinitions}
                                            </SelectItem>
                                        ) : (
                                            definitions.map((def) => (
                                                <SelectItem key={def.id} value={def.id}>
                                                    {def.displayName}
                                                </SelectItem>
                                            ))
                                        )}
                                    </SelectContent>
                                </Select>
                            </Stack>
                            <Stack gap="sm">
                                <Label htmlFor="value-template">{i18n.testData.valueTemplate}</Label>
                                <Input
                                    id="value-template"
                                    value={valueTemplate}
                                    onChange={(e) => setValueTemplate(e.target.value)}
                                    placeholder={i18n.testData.valueTemplatePlaceholder}
                                    data-testid="input-value-template"
                                />
                                <MutedText size="xs">{i18n.testData.valueTemplateHelp}</MutedText>
                            </Stack>
                        </DetailGrid>
                        <InlineRow gap="lg" className="mt-4">
                            <Button
                                onClick={handleGenerateAttributes}
                                disabled={attrMutation.isPending || !definitionId}
                                data-testid="btn-generate-attributes"
                            >
                                {attrMutation.isPending && <ButtonSpinner/>}
                                {attrMutation.isPending ? i18n.testData.generating : i18n.testData.generate}
                            </Button>
                        </InlineRow>
                    </CardContent>
                </Card>

                {/* Step 3: Generate Error Logs */}
                <Card data-testid="generate-error-logs-card">
                    <CardHeader>
                        <CardTitle data-testid="card-title-generate-error-logs">{i18n.testData.generateErrorLogs}</CardTitle>
                        <CardDescription>{i18n.testData.generateErrorLogsDescription}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button
                            onClick={() => errorLogMutation.mutate()}
                            disabled={errorLogMutation.isPending}
                            data-testid="btn-generate-error-logs"
                        >
                            {errorLogMutation.isPending && <ButtonSpinner/>}
                            {errorLogMutation.isPending ? i18n.testData.generating : i18n.testData.generate}
                        </Button>
                    </CardContent>
                </Card>
            </Stack>
        </SectionContainer>
    )
}
